import { SHTypographyProps } from "@components/design-systems";
import { CheckCircleSVG, CrossCircleSVG, WarningSVG } from "@components/svgs";
import { FieldTypeId } from "@models/configuration";
import { MAAnalysisDataDTO } from "@models/managed-accounts/entities/step/fee";
import { ManagedAccountFieldType } from "@models/managed-accounts/enums/fieldType";
import { AnalysisDataDTO } from "@models/platform-analysis/entities/steps/feature";
import { AnalysisDecisionStatus } from "@models/platform-analysis/enums/feature/decisionStatus";
import { FeatureAnalysisRowLevel } from "@models/platform-analysis/enums/feature/featureAnalysisRowLevel";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { SxProps, Theme } from "@mui/material";
import { theme } from "@themes";
import { FeatureProductCell } from "./components/rendered-items/feature-cells/product";
import { FeatureSubProductCell } from "./components/rendered-items/feature-cells/sub-product";
import { GroupScoreProductCell } from "./components/rendered-items/group-cells/product";
import { GroupSubProductValueCell } from "./components/rendered-items/group-cells/sub-product";
import { SubGroupProductScoreCell } from "./components/rendered-items/sub-group-cells/product";
import { SubGroupSubProductValueCell } from "./components/rendered-items/sub-group-cells/sub-product";
import { CurrencyCell } from "./components/rendered-items/value-renders/currency-cell";
import { DateTimeCell } from "./components/rendered-items/value-renders/date-time-cell";
import { DropdownCell } from "./components/rendered-items/value-renders/dropdown-cell";
import { LongTextCell } from "./components/rendered-items/value-renders/long-text-cell";
import { PercentageCell } from "./components/rendered-items/value-renders/percentage-cell";
import { ShortTextCell } from "./components/rendered-items/value-renders/short-text-cell";
import { SimpleNumberCell } from "./components/rendered-items/value-renders/static-number-cell";
import { WarningCell } from "./components/rendered-items/value-renders/warning-cell";
import { YesNoCell } from "./components/rendered-items/value-renders/yes-no-cell";
import { YesNoTextCell } from "./components/rendered-items/value-renders/yes-no-text-cell";

export class TableVirtualizationConfigs {
  static readonly DefaultRowOverscan = 1;
  static readonly DefaultColumnOverscan = 2;
  static readonly FireFoxRowOverscan = 10;
  static readonly FireFoxColumnOverscan = 10;
  static readonly NumOfStickyColumn = 1;
  static readonly NumOfItemsToCenter = 5;
  static readonly DefaultStepperHeight = 135;
  static readonly DefaultTableHeaderHeight = 135;
  static readonly MinimumTableHeaderHeight = 100;
  static readonly DefaultRowHeight = 40;
  static readonly DefaultColumnWidth = 190;
  static readonly DefaultBooleanIconWidth = 15;
  static readonly SubProductColumnWidth = 155;
  static readonly ExpandColumnWidth = 200;
  static readonly TitleColumnWidth = 315;
  static readonly ExpandTitleColumnWidth = 325;
}

export const TableProductCellColorMapping: {
  [key in AnalysisDecisionStatus]: string;
} = {
  [AnalysisDecisionStatus.Undecided]: theme.palette.decisionStatus.undecided,
  [AnalysisDecisionStatus.Shortlisted]:
    theme.palette.decisionStatus.shortlisted,
  [AnalysisDecisionStatus.Disqualified]:
    theme.palette.decisionStatus.disqualified,
  [AnalysisDecisionStatus.MissingEssential]:
    theme.palette.decisionStatus.missingEssential,
};

export const TableRenderedCellBackgroundColorMapping: {
  [key in AnalysisDecisionStatus]: string | undefined;
} = {
  [AnalysisDecisionStatus.Undecided]: undefined,
  [AnalysisDecisionStatus.Shortlisted]:
    TableProductCellColorMapping[AnalysisDecisionStatus.Shortlisted],
  [AnalysisDecisionStatus.Disqualified]:
    TableProductCellColorMapping[AnalysisDecisionStatus.Disqualified],
  [AnalysisDecisionStatus.MissingEssential]:
    TableProductCellColorMapping[AnalysisDecisionStatus.MissingEssential],
};

export const TableProductCellDecisionBannerMapping: {
  [key in AnalysisDecisionStatus]?: {
    title: string;
    bgColor: string;
    startIcon: JSX.Element;
  };
} = {
  [AnalysisDecisionStatus.Disqualified]: {
    title: "Disqualified",
    bgColor: theme.palette.text.disabled,
    startIcon: <CrossCircleSVG />,
  },
  [AnalysisDecisionStatus.Shortlisted]: {
    title: "Added to shortlist",
    bgColor: theme.palette.success.light,
    startIcon: <CheckCircleSVG />,
  },
  [AnalysisDecisionStatus.MissingEssential]: {
    title: "Missing essential features",
    bgColor: theme.palette.error[300],
    startIcon: <WarningSVG />,
  },
};

export const RowTitleRenderConfigs: {
  [key in FeatureAnalysisRowLevel]?: {
    cellStyles?: SxProps<Theme>;
    titleProps?: SHTypographyProps;
  };
} = {
  [FeatureAnalysisRowLevel.Section]: {
    titleProps: { variant: "subtitle2" },
  },
  [FeatureAnalysisRowLevel.SubSection]: {
    titleProps: { variant: "subtitle2", fontSize: 13 },
  },
  [FeatureAnalysisRowLevel.Feature]: {
    titleProps: { variant: "body3", colorVariant: "third" },
  },
};

export const RenderedProductCellComponentMap: Record<
  FeatureAnalysisRowLevel,
  React.MemoExoticComponent<
    ({
      analysisData,
      name,
    }: {
      analysisData: AnalysisDataDTO;
      name?: string;
    }) => JSX.Element | null
  >
> = {
  [FeatureAnalysisRowLevel.Feature]: FeatureProductCell,
  [FeatureAnalysisRowLevel.SubSection]: SubGroupProductScoreCell,
  [FeatureAnalysisRowLevel.Section]: GroupScoreProductCell,
};

export const RenderedSubProductCellComponentMap: Record<
  FeatureAnalysisRowLevel,
  React.MemoExoticComponent<
    ({
      analysisData,
      variableName,
    }: {
      analysisData: MAAnalysisDataDTO;
      variableName?: AnalysisVariable;
    }) => JSX.Element | null
  >
> = {
  [FeatureAnalysisRowLevel.Feature]: FeatureSubProductCell,
  [FeatureAnalysisRowLevel.SubSection]: SubGroupSubProductValueCell,
  [FeatureAnalysisRowLevel.Section]: GroupSubProductValueCell,
};

export const RenderedProductCellByTypeComponentMap: Partial<
  Record<
    FieldTypeId,
    React.MemoExoticComponent<
      ({ analysisData }: { analysisData: AnalysisDataDTO }) => JSX.Element
    >
  >
> = {
  [FieldTypeId.TextShort]: ShortTextCell,
  [FieldTypeId.Number]: SimpleNumberCell,
  [FieldTypeId.TextLong]: LongTextCell,
};

export const RenderSubProductCellByTypeComponentMap: Partial<
  Record<
    ManagedAccountFieldType,
    React.MemoExoticComponent<
      ({
        analysisData,
        variableName,
      }: {
        analysisData: MAAnalysisDataDTO;
        variableName?: AnalysisVariable;
      }) => JSX.Element
    >
  >
> = {
  [ManagedAccountFieldType.DateTime]: DateTimeCell,
  [ManagedAccountFieldType.Currency]: CurrencyCell,
  [ManagedAccountFieldType.YesNo]: YesNoCell,
  [ManagedAccountFieldType.YesNoText]: YesNoTextCell,
  [ManagedAccountFieldType.Percentage]: PercentageCell,
  [ManagedAccountFieldType.Warning]: WarningCell,
  [ManagedAccountFieldType.Dropdown]: DropdownCell,
  [ManagedAccountFieldType.Text]: DropdownCell,
};
